import { Key, ClipboardCheck, Unlock, PhoneCall } from "lucide-react";
import { TierCard, type Tier } from "./PackageTabs";

import { webDesignTiers, leadGenTiers, type PricingTier } from "@/lib/pricingData";

const websiteAudience = [
  "New businesses that need a professional website up and running.",
  "Established businesses ready for an updated, more professional website.",
  "Established companies offering multiple services across multiple cities.",
];
const marketingAudience = [
  "Local businesses focused on Google Search and Google Maps visibility.",
  "Growing businesses ready to add AI search and social media visibility.",
  "Established companies ready for paid ads and reputation management.",
];
const featureTitles: Record<string, string> = {
  "4 custom-designed pages (Home, About, Services, Contact)": "4 Custom Pages",
  "SEO-ready build — clean code, fast, indexable, ready for any SEO you choose": "SEO-Ready Build",
  "Lead form + click-to-call buttons": "Lead Form & Calls",
  "1 year Cloudflare hosting + SSL": "1 Year Hosting",
  "Up to 10 custom-designed pages": "10 Custom Pages",
  "Foundation SEO done for you (titles, meta, schema, sitemap)": "Foundation SEO",
  "Analytics setup": "Analytics Setup",
  "Built-in traffic dashboard — see your traffic and pages anytime": "Traffic Dashboard",
  "Up to 50 custom-designed pages": "50 Custom Pages",
  "Service × city page architecture built to rank": "Service × City Pages",
  "Advanced SEO & AI setup": "Advanced SEO & AI",
};
const featureDescriptions: Record<string, string> = {
  "4 custom-designed pages (Home, About, Services, Contact)":
    "The four pages every new business needs: a Home page that makes your offer clear, an About page that builds trust, a Services page that explains what you do, and a Contact page that turns visitors into calls.",
  "SEO-ready build — clean code, fast, indexable, ready for any SEO you choose":
    "Your site is built with clean, semantic code, fast load times, proper headings, and an XML sitemap — so Google can crawl and index every page. It's a solid technical foundation for whatever SEO work you choose to do now or later.",
  "Lead form + click-to-call buttons":
    "A contact form with spam protection plus tap-to-call buttons on mobile, so visitors can reach you in one tap from any page.",
  "1 year Cloudflare hosting + SSL":
    "Twelve months of fast, secure hosting on Cloudflare's global network with a free SSL certificate included. After the first year, hosting renews at a simple annual rate.",
  "Up to 10 custom-designed pages":
    "Room to grow: dedicated pages for each of your core services, a reviews page, and anything else your business needs — up to ten pages designed and built for you.",
  "Foundation SEO done for you (titles, meta, schema, sitemap)":
    "We write keyword-focused page titles and meta descriptions, add local business schema markup, and submit your sitemap — the on-page SEO fundamentals, completed before launch.",
  "Analytics setup":
    "We install and configure analytics so you can see where visitors come from, which pages they view, and how they contact you — from day one.",
  "Built-in traffic dashboard — see your traffic and pages anytime":
    "Your own login to a simple dashboard showing website traffic and page performance whenever you want to check — no waiting on reports from us.",
  "Up to 50 custom-designed pages":
    "A large-scale site with up to fifty pages — enough to cover every service you offer in every city you serve, all designed and built for you.",
  "Service × city page architecture built to rank":
    "A proven page structure that pairs each service with each city you serve, giving Google a clear, dedicated page to rank for searches like 'plumber in Castle Rock.'",
  "Advanced SEO & AI setup":
    "Everything in Foundation SEO plus advanced on-page optimization and AI search setup — structured content and schema that help ChatGPT, Gemini, Perplexity, and Google AI Overviews understand and cite your business.",
  "Local SEO":
    "Ongoing optimization of your website so you rank higher in Google Search for the services you offer in your area — keyword targeting, content improvements, and technical upkeep.",
  "Google Maps rankings":
    "Google Business Profile optimization, citation building, and review strategy work aimed at getting your business into the local Map Pack where nearby customers click.",
  "AI & GEO optimization":
    "Generative engine optimization: we structure your content and business data so AI assistants like ChatGPT, Gemini, and Perplexity — and Google's AI Overviews — recommend and cite your business.",
  "Social Media Marketing":
    "Branded posts created and published for you on Facebook and Instagram, keeping your business looking active, professional, and trustworthy to anyone who checks you out.",
  "Multi-city local SEO":
    "Local SEO across every city you serve — dedicated optimization for each service area so you show up in searches across your whole footprint, not just one town.",
  "PPC ad management":
    "We set up, manage, and troubleshoot your paid ads across platforms — Google LSA, Google Ads, Microsoft Ads, streaming/CTV, YouTube, and more — focused on qualified phone calls. Ad spend not included.",
  "Reputation management":
    "We monitor your online reviews, help you generate new ones from happy customers, and flag issues early — so your star rating keeps working for you.",
};
function cardTier(t: PricingTier, index: number, monthly: boolean): Tier {
  return {
    name: t.name, price: t.price, period: t.period, tag: "", desc: t.desc,
    whoFor: (monthly ? marketingAudience : websiteAudience)[index],
    accent: index === 1 ? "gold" : index === 0 ? "slate" : "charcoal",
    ctaStyle: monthly ? "peak" : "gold", featured: index === 1,
    badge: index === 1 ? "Most Popular" : undefined,
    includes: "Package includes",
    priceFootnote: monthly && index === 2 ? "Ad spend not included." : undefined,
    features: t.features.map((text) => ({ t: featureTitles[text] ?? text, d: featureDescriptions[text] ?? text })),
  };
}
export const websiteCards = webDesignTiers.map((t, i) => cardTier(t, i, false));
export const marketingCards = leadGenTiers.map((t, i) => cardTier(t, i, true));
const unifiedTiers = [websiteCards[1], marketingCards[1]];

export function UnifiedTiers() {
  return (
    <>
    <section id="packages" className="scroll-mt-24 bg-background pt-6 pb-16 lg:pt-10 lg:pb-20">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-block rounded-full border border-brand-gold-ink/40 bg-brand-gold-ink/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-brand-gold-ink">
            Choose Your Path
          </span>
          <h2 className="mt-4 text-3xl font-extrabold text-foreground sm:text-4xl">
            Pick the package that fits<br />
            <span className="text-gradient-gold-ink">Your business needs</span>
          </h2>
        </div>

        {/* Trust strip */}
        <p className="mt-6 text-center text-sm text-muted-foreground">
          <span className="inline-flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            <span className="inline-flex items-center gap-1.5">
              <ClipboardCheck className="h-4 w-4 text-brand-gold-deep" />
              <span className="font-semibold text-foreground">Clear Package Options</span>
            </span>
            <span className="text-muted-foreground/70">·</span>
            <span className="inline-flex items-center gap-1.5">
              <Key className="h-4 w-4 text-brand-gold-deep" />
              <span className="font-semibold text-foreground">Transparent Pricing</span>
            </span>
            <span className="text-muted-foreground/70">·</span>
            <span className="inline-flex items-center gap-1.5">
              <Unlock className="h-4 w-4 text-brand-gold-deep" />
              <span className="font-semibold text-foreground">You Own Your Website</span>
            </span>
          </span>
        </p>


        <h2 id="website-pricing" className="scroll-mt-36 mt-14 text-center text-2xl font-extrabold text-foreground">Web Design Packages <span className="text-base font-semibold text-muted-foreground">· one-time</span></h2>
        <p className="mt-3 text-center text-base text-muted-foreground">Every package includes 1 year of hosting and your own built-in traffic dashboard.</p>
        <p className="mt-2 text-center text-sm text-muted-foreground">Typical websites are built in less than 7 business days. Larger or custom projects are scoped before we start.</p>
        <div className="mx-auto mt-12 grid max-w-md items-stretch gap-8 sm:max-w-xl lg:max-w-none lg:grid-cols-3 lg:gap-6 xl:gap-8">
          {websiteCards.map((t) => <TierCard key={t.name} t={t} ctaLabel="Request a Quote" ctaVariant="proposal" />)}
        </div>
      </div>
    </section>

    {/* Phone banner — for people who'd rather just talk to someone */}
    <div className="bg-gradient-peak">
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-center gap-2 px-5 py-6 text-center lg:flex-row lg:gap-8 lg:px-8">
        <span className="flex items-center gap-3">
          <PhoneCall className="h-6 w-6 shrink-0 text-brand-cream" />
          <span className="text-lg font-bold text-brand-cream">Prefer to talk it through?</span>
        </span>
        <a
          href="tel:+17194193935"
          className="text-2xl font-extrabold tracking-tight text-brand-cream underline decoration-brand-cream/40 underline-offset-4 hover:decoration-brand-cream"
          aria-label="Call (719) 419-3935"
        >
          (719) 419-3935
        </a>
        <span className="text-sm font-medium text-brand-cream/80">Talk directly with Chris · no obligation</span>
      </div>
    </div>

    <section id="lead-generation-band" className="bg-background py-14 lg:py-20">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <h2 id="lead-generation" className="scroll-mt-36 text-center text-2xl font-extrabold text-foreground">Monthly Marketing Packages <span className="text-base font-semibold text-muted-foreground">· monthly</span></h2>
        <p className="mt-3 text-center text-base text-muted-foreground">More services at each level. Month-to-month. Website builds are separate.</p>
        <div className="mx-auto mt-12 grid max-w-md items-stretch gap-8 sm:max-w-xl lg:max-w-none lg:grid-cols-3 lg:gap-6 xl:gap-8">
          {marketingCards.map((t) => <TierCard key={t.name} t={t} ctaLabel="Request a Quote" ctaVariant="proposal" />)}
        </div>
        <p className="mt-8 text-center text-sm text-muted-foreground">
          Want extra pages, e-commerce, or CRM setup?{" "}
          <a href="#add-ons" className="font-semibold text-foreground underline underline-offset-4 hover:text-brand-gold-deep">
            Customize your plan ↓
          </a>
        </p>
      </div>
    </section>
    </>
  );
}

export { unifiedTiers };
